import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useStore } from '../store/useStore';
import { toast } from 'sonner';

export function useChatSocket() {
  const activeChannelId = useStore((state) => state.activeChannel?.id);
  const {
    addMessage,
    setSocket,
    socket,
    setPresence,
    updateActiveTaskData,
    incrementUnread,
  } = useStore();

  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('tm_jwt');
    if (!token) return;

    // Resolve Backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    const s = io(backendUrl, {
      path: '/api/ws',
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnectionDelay: 2000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = s;
    setSocket(s);

    s.on('connect', () => {
      console.log('[socket.io] Connected successfully!');
      if (activeChannelId) {
        s.emit('chat:join_channel', { channelId: activeChannelId });
      }
    });

    s.on('connect_error', (err) => {
      console.error('[socket.io] Connection error:', err.message);
      if (err.message.toLowerCase().includes('authentication') || err.message.toLowerCase().includes('token')) {
        localStorage.removeItem('tm_jwt');
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/signup')) {
          window.location.href = '/login';
        }
      }
    });

    const getChatSettings = () => {
      try {
        const saved = localStorage.getItem('chat_settings');
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.error(e);
      }
      return {
        allowNotifications: true,
        allowReactions: true,
        notificationSound: 'tones',
        emailNotifications: true,
        smartCompose: true,
        smartReply: true,
        autoCorrect: true,
        conversationSummary: true,
        liveMarkdown: true,
        theme: 'light',
        density: 'comfortable',
        accentColor: 'sapphire'
      };
    };

    s.on('chat:receive', (msg) => {
      console.log('[socket.io] Received message:', msg);
      const chanId = msg.channelId || msg.channel_id;
      const currentActiveChannelId = useStore.getState().activeChannel?.id;
      
      const me = useStore.getState().activeUser;
      const msgSenderId = msg.senderId || msg.sender_id;

      // Append if it's for current channel
      if (chanId === currentActiveChannelId) {
        if (msg.parent_id) {
          // Increment parent reply count in main feed
          useStore.setState((state) => ({
            messages: state.messages.map(m => m.id === msg.parent_id ? { ...m, reply_count: (m.reply_count || 0) + 1 } : m)
          }));
          // Broadcast to active thread panel
          const customEvent = new CustomEvent(`chat:thread_reply:${msg.parent_id}`, { detail: msg });
          window.dispatchEvent(customEvent);
        } else {
          addMessage(msg);
        }
        
        // Emit read confirmation if it's from someone else
        if (me && msgSenderId !== me.id) {
          s.emit('message:read', { channelId: currentActiveChannelId });
        }
      } else {
        // Increment unread count only if it's not from ourselves
        if (chanId && (!me || msgSenderId !== me.id)) {
          incrementUnread(chanId);
        }
      }

      // Check settings and play sound / show notification
      if (me && msgSenderId !== me.id) {
        const mutedChannels = JSON.parse(localStorage.getItem('muted_channel_ids') || '[]');
        if (mutedChannels.includes(chanId)) {
          return;
        }

        const settings = getChatSettings();
        
        // 1. Toast & Browser Notification
        if (settings.allowNotifications) {
          const senderName = msg.sender_name || 'Someone';
          const msgText = msg.message_text || msg.messageText || 'Sent an attachment';

          toast(`${senderName}: ${msgText}`, {
            action: {
              label: 'Open',
              onClick: () => {
                if (chanId && chanId !== currentActiveChannelId) {
                  const channels = useStore.getState().channels;
                  const targetChan = channels.find(c => c.id === chanId);
                  if (targetChan) {
                    useStore.setState({ activeChannel: targetChan });
                  }
                }
              }
            }
          });

          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification(senderName, {
              body: msgText,
              icon: '/favicon.ico'
            });
          }
        }

        // 2. Play Notification Sound
        if (settings.notificationSound && settings.notificationSound !== 'none') {
          let soundUrl = '';
          if (settings.notificationSound === 'tones') {
            soundUrl = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-84.wav';
          } else if (settings.notificationSound === 'chime') {
            soundUrl = 'https://assets.mixkit.co/active_storage/sfx/2019/2019-84.wav';
          } else if (settings.notificationSound === 'subtle') {
            soundUrl = 'https://assets.mixkit.co/active_storage/sfx/2568/2568-84.wav';
          }
          if (soundUrl) {
            const audio = new Audio(soundUrl);
            audio.volume = 0.35;
            audio.play().catch(e => console.log('Audio playback blocked/failed:', e));
          }
        }
      }
    });

    s.on('chat_message_reaction_changed', (data) => {
      const { message_id, reactions } = data;
      useStore.setState((state) => ({
        messages: state.messages.map(m => m.id === message_id ? { ...m, reactions } : m)
      }));
      const customEvent = new CustomEvent(`chat:message_reaction_changed:${message_id}`, { detail: reactions });
      window.dispatchEvent(customEvent);
    });

    s.on('typing:status', (data) => {
      const { channelId, userId, isTyping } = data;
      const allUsers = useStore.getState().users;
      const user = allUsers.find(u => u.id === userId);
      const userName = user ? user.name : 'Someone';
      useStore.getState().setTypingStatus(channelId, userId, userName, isTyping);
    });

    s.on('presence:update', (data) => {
      const { userId, status } = data;
      setPresence(userId, status);
    });

    s.on('notification:new', (notif) => {
      toast(notif.title, {
        description: notif.message,
      });
      // Increment general unread counter or specific channel unread
      if (notif.entity_type === 'message' && notif.channel_id) {
        incrementUnread(notif.channel_id);
      }
    });

    s.on('task:update', (data) => {
      const { taskId, changes } = data;
      const currentTaskId = useStore.getState().activeTaskId;
      if (currentTaskId && String(currentTaskId) === String(taskId)) {
        updateActiveTaskData(changes);
        toast('Task Updated', {
          description: `The task you are viewing was updated by another user.`
        });
      }
    });

    s.on('message:read_receipt', (data) => {
      const { channelId, userId } = data;
      // Mark all messages in this channel as read by other users
      useStore.setState((state) => {
        const updated = state.messages.map(m => {
          const mChanId = m.channelId || m.channel_id;
          const mSenderId = m.senderId || m.sender_id;
          if (mChanId === channelId && mSenderId !== userId) {
            return { ...m, status: 'read' };
          }
          return m;
        });
        return { messages: updated };
      });
    });

    s.on('message:deleted', (data) => {
      const { id } = data;
      useStore.setState((state) => ({
        messages: state.messages.filter(m => m.id !== id)
      }));
    });

    s.on('message:updated', (data) => {
      const { id, message_text } = data;
      useStore.setState((state) => ({
        messages: state.messages.map(m => m.id === id ? { ...m, message_text, messageText: message_text } : m)
      }));
    });

    s.on('huddle:incoming_call', (data) => {
      // data: { channelId, callerId, callerName, callerAvatar }
      const settings = getChatSettings();
      if (settings.notificationSound && settings.notificationSound !== 'none') {
        const audio = new Audio('/sounds/call-ring.mp3'); // Try to play a ringtone if exists
        audio.play().catch(() => {}); // ignore autoplay restrictions
      }
      useStore.setState({ incomingCall: data });
    });

    s.on('huddle:call_rejected', (data) => {
      // data: { channelId, rejecterId }
      toast('Call was declined.');
    });

    s.on('disconnect', () => {
      console.log('[socket.io] Disconnected');
    });

    return () => {
      s.disconnect();
      setSocket(null);
    };
  }, []);

  // Separate effect to handle room joining on channel switches
  useEffect(() => {
    if (socket && activeChannelId) {
      console.log('[socket.io] Joining channel:', activeChannelId);
      socket.emit('chat:join_channel', { channelId: activeChannelId });
    }
  }, [socket, activeChannelId]);

  // Emitters
  const emitTypingStart = (channelId) => {
    if (socket) socket.emit('typing:start', { channelId });
  };

  const emitTypingStop = (channelId) => {
    if (socket) socket.emit('typing:stop', { channelId });
  };

  const emitMessageRead = (channelId) => {
    if (socket) socket.emit('message:read', { channelId });
  };

  return {
    socket,
    emitTypingStart,
    emitTypingStop,
    emitMessageRead,
  };
}
